import axios from 'axios';
import { logout } from './auth';
import { setSession } from './jwt';

const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API });

// Add request interceptor to include authentication token on every request
axiosInstance.interceptors.request.use(
  (config) => {
    // Try to get the token from localStorage
    try {
      const tokenData = JSON.parse(localStorage.getItem('accessToken'));
      const accessToken = tokenData?.accessToken;

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } catch (error) {
      console.error('Error accessing token:', error);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Gestion des réponses et erreurs 401 (token expiré)
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add response interceptor to handle unauthorized errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si on reçoit une erreur 401 (Unauthorized) et qu'on n'a pas déjà essayé de refresher
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // Si on est déjà en train de rafraîchir, on met la requête en attente
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Récupérer le refresh token
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          // Si pas de refresh token, on déconnecte l'utilisateur
          logout();
          // Dispatch l'événement de session expirée
          window.dispatchEvent(new CustomEvent('session_expired'));
          return Promise.reject(error);
        }

        // Appel à l'API pour rafraîchir le token
        const response = await axios.post(
          `${process.env.REACT_APP_API}/auth/refresh`,
          {
            refresh_token: refreshToken,
          }
        );

        if (response.status === 200) {
          // Extraction des données de la réponse
          const {
            accessToken,
            refreshToken: newRefreshToken,
            user,
          } = response.data;

          // Utiliser setSession pour mettre à jour les tokens de manière cohérente
          setSession({
            accessToken,
            refreshToken: newRefreshToken,
            user,
          });

          // Mettre à jour le token dans la requête originale
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          // Traiter les requêtes en attente
          processQueue(null, accessToken);

          // Réessayer la requête originale avec le nouveau token
          return axiosInstance(originalRequest);
        } else {
          // Si le rafraîchissement échoue, on déconnecte l'utilisateur
          processQueue(error, null);
          logout();
          window.dispatchEvent(new CustomEvent('session_expired'));
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // En cas d'erreur pendant le rafraîchissement, on déconnecte l'utilisateur
        processQueue(refreshError, null);
        logout();
        window.dispatchEvent(new CustomEvent('session_expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(
      (error.response && error.response.data) || 'Something went wrong'
    );
  }
);

export default axiosInstance;
